import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, Modal, Rate, Form, Input, Checkbox, Popover } from 'antd';
import '../App.css'; // Make sure to import your CSS file
import './SaveTraining.css';
import { FireOutlined, LoadingOutlined } from '@ant-design/icons'
import axios from 'axios';
import { useDialog } from './DialogProvider';
import SyncStrava from './SyncStrava';
import { canUploadToStrava } from "../utils/trainerLink";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const tcxHelpers = <div style={{ marginTop: "2%" }}>
    You can download your activity TCX file and manually upload it to most platforms, such as <a target="_blank" rel="noreferrer" href='https://connect.garmin.com/modern/import-data'>Garmin</a> or <a target="_blank" rel="noreferrer" href='https://www.strava.com/upload/select'>Strava</a>. <br />
    You can always download the TCX file later via the "History" tab. <br />
    To save your session as 'virtual biking' on Strava, you need to upload it to Strava via the checkbox above. <br />
    After that, the TCX file will be tagged as "biking" and it won't be possible to change it to "virtual biking".
</div>


const SaveTraining = ({ disabled, weight, steps, initSteps, data, FTP, setFTP, setWeight, infoToSave, FTPResult, otherUserInfo, setOtherUserInfo, buttonStyle, buttonClassName, externalOpen = false, onModalClose, onSaved, onSavedUiConfirmed }) => {
    const { alert: alertDlg, confirm } = useDialog();

    const [tcxLink, setTcxLink] = useState("")
    const [finalWeight, setFinalWeight] = useState(weight)
    const [finalWeightText, setFinalWeightText] = useState(weight !== undefined && weight !== null ? String(weight) : "")
    const [intensity, setIntensity] = useState(2.5)
    const [comment, setComment] = useState("")
    const [fetchTcxLink, setFetchTcxLink] = useState(false)
    const [finalFTP, setFinalFTP] = useState(FTP)
    const [isLoading, setIsLoading] = useState(false)
    const [uploadToStrava, setUploadToStrava] = useState(otherUserInfo.stravaRefreshToken != null)
    const [stravaAccessCode, setStravaAccessCode] = useState("")
    const [resultModalOpen, setResultModalOpen] = useState(false)
    const [resultModalTitle, setResultModalTitle] = useState("")
    const [resultModalMessage, setResultModalMessage] = useState("")
    const [resultModalIsWarning, setResultModalIsWarning] = useState(false)
    const didSaveRef = useRef(false);

    useEffect(() => {
        // Reset per open/close to ensure we only "confirm" after a real save.
        didSaveRef.current = false;
    }, [externalOpen]);

    // Update local state when props change
    React.useEffect(() => {
        if (weight !== undefined) {
            setFinalWeight(weight);
            setFinalWeightText(weight !== null && weight !== undefined ? String(weight) : "");
        }
        if (FTP !== undefined) setFinalFTP(FTP);
        if (otherUserInfo?.stravaRefreshToken !== undefined) {
            setUploadToStrava(otherUserInfo.stravaRefreshToken != null);
        }
    }, [weight, FTP, otherUserInfo?.stravaRefreshToken]);



    const axiosInstance = axios.create({
        baseURL: '/api', // Adjust this baseURL to where your backend is hosted
        headers: {
            'Content-Type': 'application/json'
        }
    });

    // Attach token to every request if available.  Logic needs to be updated in SaveTraining.js as well + Login + App.js
    axiosInstance.interceptors.request.use(config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    }, error => {
        return Promise.reject(error);
    });

    // Handle response errors.  Logic needs to be updated in SaveTraining.js as well + Login + App.js
    axiosInstance.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;

            // Check if the error is due to an expired access token
            if (error.response && error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Prevent infinite retries

                try {
                    // Call /refresh/token to get new tokens
                    const refreshToken = localStorage.getItem('refresh_token');
                    const refreshResponse = await axios.post('api/refresh_tokens/', { refresh_token: refreshToken });

                    if (refreshResponse.status === 200) {
                        const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;

                        // Store new tokens in localStorage
                        localStorage.setItem('token', newToken);
                        localStorage.setItem('refresh_token', newRefreshToken);

                        // Update Authorization header and retry the original request
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    }
                } catch (refreshError) {
                    alertDlg("You’ve been inactive for too long. You might need to log in again.")
                    console.error('Failed to refresh token:', refreshError);
                    //localStorage.removeItem('token'); // Remove the stored token
                    //localStorage.removeItem('refresh_token'); // Remove the stored token
                    //setUsername(null)  no access to setUsername. Not logging out automatically so user can download raw data
                    //console.log('Logged out');
                    // Optionally, handle token refresh failure (e.g., logout user)
                }
            }

            return Promise.reject(error);
        }
    );

    function downloadFile(url) {
        const link = document.createElement('a');
        link.href = url;
        // Optionally set the download attribute here to a specific filename
        link.setAttribute('download', 'filename.ext'); // Choose the file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }


    const downloafTcxFile = async () => {
        const activityData = {
            data: data,
            weight: finalWeight,
            init_steps: initSteps || [],
            steps: steps,
            ftp: finalFTP,
            with_tcx_link: fetchTcxLink,
            intensity: intensity,
            comment: comment,
            strava_refresh_token: stravaPossible && uploadToStrava ? otherUserInfo.stravaRefreshToken : null,
            strava_access_code: stravaPossible && uploadToStrava ? stravaAccessCode : null,
            ...infoToSave
        }; //adding bike weight in backend
        function downloadJSON(dictionary, filename) {
            // Convert the dictionary to a JSON string
            const jsonString = JSON.stringify(dictionary, null, 2);

            // Create a Blob object from the JSON string
            const blob = new Blob([jsonString], { type: 'application/json' });

            // Create an anchor element for the download
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = filename || 'data.json';

            // Programmatically click the anchor to trigger the download
            document.body.appendChild(a);
            a.click();

            // Clean up the anchor element
            document.body.removeChild(a);
        }

        axiosInstance.post('/download_tcx_file/', activityData)
            .then(response => {
                didSaveRef.current = true;
                setTcxLink(response.data.link);
                setFTP(finalFTP);
                setWeight(finalWeight);
                setIsLoading(false);
                setStravaAccessCode("");
                setOtherUserInfo({ ...otherUserInfo, "stravaRefreshToken": response.data.strava_refresh_token || otherUserInfo.stravaRefreshToken });
                if (onSaved) onSaved();
                if (fetchTcxLink) {
                    setResultModalTitle("Download your TCX");
                    setResultModalMessage(
                        response.data.strava_error
                            ? `To proceed to file download, please click on quit on the pop-up that follows, don't worry it won't close your window. We also encountered an issue while trying to upload on Strava: ${response.data.strava_error}`
                            : "To proceed to file download, please click on quit on the pop-up that follows, don't worry it won't close your window."
                    );
                    setResultModalIsWarning(true);
                    setResultModalOpen(true);
                } else if (response.data.strava_error) {
                    setResultModalTitle("Saved with a Strava issue");
                    setResultModalMessage(`Well saved but we encountered an issue while trying to upload on Strava: ${response.data.strava_error}`);
                    setResultModalIsWarning(true);
                    setResultModalOpen(true);
                } else if (response.data.upload_id) {
                    setResultModalTitle("Success");
                    setResultModalMessage("Well saved and uploaded to Strava!");
                    setResultModalIsWarning(false);
                    setResultModalOpen(true);
                } else {
                    setResultModalTitle("Success");
                    setResultModalMessage("Well saved!");
                    setResultModalIsWarning(false);
                    setResultModalOpen(true);
                }

            })
            .catch(async error => { 
                if (await confirm('Error saving your training session. Would you like to download your raw data instead? If this error persists even after checking your internet connection, you can send this file to sam@simplyride.app to recover your session. I apologize for the inconvenience and appreciate your help in reporting this bug to prevent it from happening in the future. Thanks!')) {
                    downloadJSON(activityData, "simplyride_session.json"); 
                }
                setTcxLink(""); 
                setIsLoading(false); 
                console.log("error saving session", error); 
            });
    };

    useEffect(() => {
        tcxLink && tcxLink !== "" && downloadFile(tcxLink)
    }, [tcxLink]);

    useEffect(() => {
        const newComment = FTPResult === null ? "" : ` (Estimated FTP results : ${FTPResult} Watts)`
        setComment((prevComment) => prevComment.split(" (Estimated FTP results")[0] + newComment)
    }, [FTPResult]);



    useEffect(() => {
        console.log("mounted")
    }, []);

    const weightHelpText = "This is needed to estimate your speed. This value will be set as default.";
    const weightTrimmed = (finalWeightText ?? "").trim();
    const weightIsIntegerText = /^\d+$/.test(weightTrimmed);
    const weightParsed = weightIsIntegerText ? parseInt(weightTrimmed, 10) : null;
    const weightIsValid = Number.isInteger(weightParsed) && (weightParsed ?? 0) > 0;
    const weightHasError = !weightIsValid;
    const saveDisabled = Boolean(disabled) || isLoading || !weightIsValid;

    // A ride with no trainer has no power, no cadence and no distance. There is
    // nothing to publish, so the choice is removed rather than greyed out: an
    // explanation of why the box is disabled costs more words than the box.
    const stravaPossible = canUploadToStrava({ link: infoToSave?.trainer_link });

    return <>
       
        <Modal
            title="Save training"
            cancelText={"Cancel"}
            cancelType="default"
            okButtonProps={{ style: { display: 'none' } }}
            open={externalOpen}
            onCancel={() => {
                onModalClose && onModalClose();
            }}
            rootClassName="liquid-glass-modal save-training-modal"
            centered
            width={720}
            styles={{ mask: { backdropFilter: 'blur(8px) saturate(120%)', backgroundColor: 'rgba(255,255,255,0.15)' } }}
        >
            <Form
                className="glass-form"
                onFinish={() => {
                    if (!weightIsValid) {
                        alertDlg("Please set your weight (a positive integer) before saving.");
                        return;
                    }
                    setIsLoading(true);
                    downloafTcxFile();
                }}
                initialValues={{ weight: finalWeightText, comment, intensity, finalFTP, stravaAccessCode }}
            >
                <Row style={{ marginTop: "5%" }} >
                    <Col span={10}>
                        <Form.Item label={< FireOutlined />} >
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <Rate allowHalf={true} value={intensity} onChange={(v) => v >= 0 && setIntensity(v)} />
                            </div>
                        </Form.Item>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={13}>
                        <Form.Item name="comment" label="" >
                            <Input.TextArea block onChange={(e) => setComment(e.target.value)} placeholder='Comment'></Input.TextArea>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={11}>
                        <Form.Item
                            style={{ marginTop: "5%" }}
                            name="weight"
                            label="Weight"
                            validateStatus={weightHasError ? "error" : ""}
                            help={<span style={{ color: weightHasError ? "#ff4d4f" : undefined }}>{weightHelpText}</span>}
                        >
                            <Input
                                type="number"
                                min={1}
                                step={1}
                                value={finalWeightText}
                                onChange={(e) => {
                                    const nextText = e.target.value;
                                    setFinalWeightText(nextText);

                                    const nextTrimmed = (nextText ?? "").trim();
                                    if (nextTrimmed === "") {
                                        setFinalWeight(undefined);
                                        return;
                                    }

                                    if (!/^\d+$/.test(nextTrimmed)) {
                                        setFinalWeight(undefined);
                                        return;
                                    }

                                    const nextParsed = parseInt(nextTrimmed, 10);
                                    if (Number.isInteger(nextParsed) && nextParsed > 0) {
                                        setFinalWeight(nextParsed);
                                    } else {
                                        setFinalWeight(undefined);
                                    }
                                }}
                                placeholder="Weight (kg)"
                                addonAfter="kg"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={2}></Col>
                    <Col span={11}>
                        <Form.Item style={{ marginTop: "5%" }} name="finalFTP" label="FTP" help={FTPResult ? `This value will be set as default.Feel free to update it to ${FTPResult}` : "This value will be set as default."} >
                            <Input
                                type="number"
                                onChange={(e) => e.target.value > 0 && setFinalFTP(parseInt(e.target.value))}
                                placeholder="FTP"
                                addonAfter="Watt"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                {stravaPossible && (
                    <Row>
                        <Col span={12}>
                            <Form.Item style={{ marginTop: "2%" }} name="uploadToStrava" value={uploadToStrava} label="Upload activity to Strava ?" >
                                {((otherUserInfo.stravaRefreshToken === null || otherUserInfo.stravaRefreshToken === undefined) || otherUserInfo.stravaRefreshToken === "invalid_refresh_token") && stravaAccessCode === "" ?
                                    < Popover content="You need to sync your Strava account first" >
                                        <Checkbox

                                            checked={false}
                                            disabled={true}
                                            onChange={(e) => setUploadToStrava(e.target.checked)}
                                        />
                                    </Popover> :
                                    <Checkbox
                                        checked={uploadToStrava}
                                        onChange={(e) => setUploadToStrava(e.target.checked)}
                                    />
                                }
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item style={{ marginBottom: 0 }} name="stravaAccessCode" label="">
                                <SyncStrava
                                    axiosInstance={axiosInstance}
                                    otherUserInfo={otherUserInfo}
                                    setOtherUserInfo={setOtherUserInfo}
                                    code={stravaAccessCode}
                                    setCode={setStravaAccessCode}
                                    headerWidth="100%"
                                    headerBackground="#fd824e"
                                    contentBackground="var(--st-surface)"
                                    onCodeNonEmptyChange={(nonEmpty) => setUploadToStrava(Boolean(nonEmpty))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col span={12}>
                        <Button
                            block
                            className="primary-glass-btn"
                            style={{ align: "right", marginTop: "5px" }}
                            type="primary"
                            htmlType="submit"
                            disabled={saveDisabled}
                            name="noTcx"
                            onClick={() => setFetchTcxLink(false)}>
                            {isLoading && !fetchTcxLink ? <LoadingOutlined spin={true} /> : "Save"}
                        </Button>
                    </Col>
                    <Col span={12}>
                        <Button
                            block
                            className="glass-cta"
                            style={{ align: "right", marginTop: "5px", marginLeft: "2%" }}
                            type="default"
                            htmlType="submit"
                            disabled={saveDisabled}
                            name="withTcx"
                            onClick={() => setFetchTcxLink(true)}>
                            {isLoading && fetchTcxLink ? <LoadingOutlined spin={true} /> : "Save and get TCX File"}
                        </Button>
                    </Col>

                </Row>

            </Form>
            <i>{tcxHelpers}</i>

        </Modal>

        <Modal
            title={resultModalTitle || (resultModalIsWarning ? "Notice" : "Success")}
            open={resultModalOpen}
            rootClassName="liquid-glass-modal save-training-modal"
            onCancel={() => setResultModalOpen(false)}
            footer={[
                <Button
                    key="ok"
                    type="primary"
                    className="primary-glass-btn"
                    onClick={() => {
                        setResultModalOpen(false);
                        // Close the save modal first, then open any post-save UI (dev feedback, etc.)
                        onModalClose && onModalClose();
                        if (didSaveRef.current && onSavedUiConfirmed) {
                            try {
                                setTimeout(() => {
                                    try { onSavedUiConfirmed(); } catch { }
                                }, 0);
                            } catch { }
                        }
                        didSaveRef.current = false;
                    }}
                >
                    OK
                </Button>,
            ]}
        >
            <p>{resultModalMessage}</p>
        </Modal>

    </>;
};

export default SaveTraining;

